import type { JSONContent } from '@tiptap/core';
import type { CSSProperties } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';

import type { RichTextEditorExtensionOptions } from './richTextEditorExtensions';
import type { RichTextToolbarItem } from './richTextToolbar';

export const EMPTY_RICH_TEXT_DOC: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

export const HEADING_LEVEL_OPTIONS = ['h1', 'h2', 'h3', 'h4'] as const;

export function updateById<T extends { id: string }>(
  items: readonly T[],
  itemId: string,
  updater: (item: T) => T
): T[] {
  return items.map((item) => (item.id === itemId ? updater(item) : item));
}

export function reorderById<T extends { id: string | number }>(
  items: readonly T[],
  activeId: string | number,
  overId: string | number
): T[] {
  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return [...items];
  }

  const reordered = [...items];
  const [movedItem] = reordered.splice(oldIndex, 1);

  if (!movedItem) {
    return [...items];
  }

  reordered.splice(newIndex, 0, movedItem);
  return reordered;
}

export interface InteractionRichTextToolbarStyles {
  toolbar?: CSSProperties;
  button?: CSSProperties;
  activeButton?: CSSProperties;
  divider?: CSSProperties;
}

export function createTokenRichTextToolbarStyles(
  tokens: ThemeTokens
): InteractionRichTextToolbarStyles {
  return {
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: tokens.spacing.xs,
      marginBottom: tokens.spacing.sm,
      paddingBottom: tokens.spacing.sm,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    button: {
      minWidth: '32px',
      height: '32px',
      padding: `0 ${tokens.spacing.sm}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.muted,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    activeButton: {
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
    },
    divider: {
      width: '1px',
      height: '20px',
      backgroundColor: tokens.borders.default,
      margin: `0 ${tokens.spacing.xs}`,
    },
  };
}

export type InteractionRichTextToolbarPreset =
  | 'basic'
  | 'basicWithLists'
  | 'basicWithListsAndUnderline'
  | 'assessmentWithLinks'
  | 'assessmentWithLinksAndRule'
  | 'full'
  | 'fullNoFontSize';

export interface InteractionRichTextFieldPresetConfig {
  toolbarItems: RichTextToolbarItem[];
  extensionOptions: RichTextEditorExtensionOptions;
}

export const DEFAULT_WCAG_BACKGROUND_COLORS = [
  '#FFFFFF',
  '#EDEDED',
  '#F1F1F1',
  '#171932',
];

export function getInteractionRichTextFieldPresetConfig(
  preset: InteractionRichTextToolbarPreset,
  backgroundColors: string[] = DEFAULT_WCAG_BACKGROUND_COLORS
): InteractionRichTextFieldPresetConfig {
  switch (preset) {
    case 'basic':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'strike' },
        ],
        extensionOptions: {},
      };
    case 'basicWithLists':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'strike' },
          { kind: 'action', action: 'bulletList', label: '*' },
          { kind: 'action', action: 'orderedList', label: '1.' },
          { kind: 'action', action: 'outdent' },
          { kind: 'action', action: 'indent' },
        ],
        extensionOptions: {
          enableLists: true,
        },
      };
    case 'basicWithListsAndUnderline':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'underline' },
          { kind: 'action', action: 'strike' },
          { kind: 'action', action: 'bulletList', label: '*' },
          { kind: 'action', action: 'orderedList', label: '1.' },
          { kind: 'action', action: 'outdent' },
          { kind: 'action', action: 'indent' },
        ],
        extensionOptions: {
          enableLists: true,
          enableUnderline: true,
        },
      };
    case 'assessmentWithLinks':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'underline' },
          { kind: 'separator' },
          { kind: 'action', action: 'bulletList', label: '≡' },
          { kind: 'action', action: 'outdent' },
          { kind: 'action', action: 'indent' },
          { kind: 'action', action: 'link', label: '🔗' },
        ],
        extensionOptions: {
          enableLists: true,
          enableUnderline: true,
          enableLinks: true,
        },
      };
    case 'assessmentWithLinksAndRule':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'underline' },
          { kind: 'separator' },
          { kind: 'action', action: 'bulletList', label: '≡' },
          { kind: 'action', action: 'outdent' },
          { kind: 'action', action: 'indent' },
          { kind: 'action', action: 'horizontalRule', label: '—' },
          { kind: 'action', action: 'link', label: '🔗' },
        ],
        extensionOptions: {
          enableLists: true,
          enableUnderline: true,
          enableLinks: true,
          enableHorizontalRule: true,
        },
      };
    case 'full':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'underline' },
          { kind: 'separator' },
          { kind: 'fontSizeDropdown' },
          { kind: 'fontColorPicker', backgroundColors },
          { kind: 'separator' },
          { kind: 'action', action: 'blockquote' },
          { kind: 'action', action: 'link', label: '🔗' },
          { kind: 'separator' },
          { kind: 'action', action: 'bulletList' },
          { kind: 'action', action: 'orderedList' },
          { kind: 'action', action: 'outdent' },
          { kind: 'action', action: 'indent' },
          { kind: 'separator' },
          { kind: 'action', action: 'clearFormatting' },
        ],
        extensionOptions: {
          enableLists: true,
          enableUnderline: true,
          enableLinks: true,
          enableBlockquote: true,
          enableTextColor: true,
          enableFontSize: true,
        },
      };
    case 'fullNoFontSize':
      return {
        toolbarItems: [
          { kind: 'action', action: 'bold' },
          { kind: 'action', action: 'italic' },
          { kind: 'action', action: 'underline' },
          { kind: 'separator' },
          { kind: 'fontColorPicker', backgroundColors },
          { kind: 'separator' },
          { kind: 'action', action: 'blockquote' },
          { kind: 'action', action: 'link', label: '🔗' },
          { kind: 'separator' },
          { kind: 'action', action: 'bulletList' },
          { kind: 'action', action: 'orderedList' },
          { kind: 'action', action: 'outdent' },
          { kind: 'action', action: 'indent' },
          { kind: 'separator' },
          { kind: 'action', action: 'clearFormatting' },
        ],
        extensionOptions: {
          enableLists: true,
          enableUnderline: true,
          enableLinks: true,
          enableBlockquote: true,
          enableTextColor: true,
        },
      };
  }
}
